import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/contexts/auth'
import { supabase } from '@/lib/supabase'

export interface MonthlyProgress {
  month: number
  monthName: string
  booksRead: number
  cumulative: number
}

export interface UseYearlyProgressReturn {
  monthlyData: MonthlyProgress[]
  currentYear: number
  totalReadThisYear: number
  isLoading: boolean
  error: Error | null
}

/**
 * Optimized hook for yearly reading progress data
 * Uses PostgreSQL stored function for maximum performance
 */
export function useYearlyProgress(): UseYearlyProgressReturn {
  const { user } = useAuth()
  const currentYear = new Date().getFullYear()

  const { data, isLoading, error } = useQuery({
    queryKey: ['yearly-progress', user?.id, currentYear],
    queryFn: async () => {
      if (!user?.id) throw new Error('User not authenticated')

      // Uses PostgreSQL stored function for optimization
      const { data, error } = await supabase.rpc('get_yearly_progress', {
        user_uuid: user.id,
        target_year: currentYear,
      })

      if (error) {
        console.error('Yearly progress error:', error)
        throw new Error(`Failed to fetch yearly progress: ${error.message}`)
      }

      return data || []
    },
    enabled: !!user?.id,
    staleTime: 10 * 60 * 1000, // 10 minutos - progresso não muda com frequência
    gcTime: 30 * 60 * 1000, // 30 minutos no cache
    retry: 2,
  })

  // Process month data with Portuguese names
  const monthNames = [
    'Jan',
    'Fev',
    'Mar',
    'Abr',
    'Mai',
    'Jun',
    'Jul',
    'Aug',
    'Set',
    'Out',
    'Nov',
    'Dez',
  ]

  const monthlyData: MonthlyProgress[] = []
  let cumulative = 0

  for (let month = 1; month <= 12; month++) {
    const monthData = data?.find((d: { month: number; books_read: number }) => d.month === month)
    const booksRead = monthData?.books_read || 0
    cumulative += booksRead

    monthlyData.push({
      month,
      monthName: monthNames[month - 1],
      booksRead,
      cumulative,
    })
  }

  const totalReadThisYear = cumulative

  return {
    monthlyData,
    currentYear,
    totalReadThisYear,
    isLoading,
    error: error as Error | null,
  }
}
